import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Fragment, useState, type ReactElement, type ReactNode } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  FiscalDocumentView,
  SalesOrderActionResult,
  SalesOrderCreated,
  SalesOrderPdfLink,
  SalesOrderStatusHistoryItem,
  SalesOrderStatusRef,
  SalesOrderView,
} from "@cx/contracts";
import {
  FREIGHT_MODE_LABELS,
  ORDER_MANUAL_ACTION_LABELS,
  formatBRL,
  formatDateBR,
  formatDocument,
  formatPct,
  formatQty,
  type OrderManualAction,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { FINANCIAL_EFFECT_LABELS, STOCK_EFFECT_LABELS, historyLabel, historySteps } from "./labels";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE } from "../invoices/labels";
import { EmitInvoiceDialog } from "../invoices/EmitInvoiceDialog";
import { ShipmentsPanel } from "../logistics/ShipmentsPanel";

const ACTION_PATH: Readonly<Record<OrderManualAction, string>> = {
  post_stock: "stock/post",
  reverse_stock: "stock/reverse",
  post_financial: "financial/post",
  reverse_financial: "financial/reverse",
};

const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/** Pedido gravado: dados, itens, totais, situação com histórico; ações de estoque/contas, PDF, duplicar, excluir. */
export function SalesOrderViewPage(): ReactElement {
  const { id } = useParams({ from: "/vendas/pedidos/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const order = useQuery({
    queryKey: ["sales-orders", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<SalesOrderView>(`/sales-orders/${id}`),
  });
  const history = useQuery({
    queryKey: ["sales-orders", companyId, "history", id],
    refetchOnMount: "always",
    queryFn: () => api<{ data: SalesOrderStatusHistoryItem[] }>(`/sales-orders/${id}/status-history`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const status = useMutation({
    mutationFn: (to: SalesOrderStatusRef) =>
      api(`/sales-orders/${id}/status`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ toStatusId: to.id, expectedFromStatusId: order.data!.status.id }),
      }),
    onSuccess: (_d, to) => {
      toast("success", `Situação alterada para ${to.label}`);
      refresh();
    },
    onError,
  });
  const act = useMutation({
    mutationFn: (a: OrderManualAction) =>
      api<SalesOrderActionResult>(`/sales-orders/${id}/${ACTION_PATH[a]}`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: (_d, a) => {
      toast("success", `${ORDER_MANUAL_ACTION_LABELS[a]}: feito`);
      refresh();
      qc.invalidateQueries({ queryKey: ["stock", companyId] });
    },
    onError,
  });
  const pdf = useMutation({
    mutationFn: () => api<SalesOrderPdfLink>(`/sales-orders/${id}/pdf`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const [dupKey] = useState(() => crypto.randomUUID());
  const duplicate = useMutation({
    mutationFn: () =>
      api<SalesOrderCreated>(`/sales-orders/${id}/duplicate`, {
        method: "POST",
        headers: { "Idempotency-Key": dupKey },
      }),
    onSuccess: (r) => {
      toast("success", `Pedido nº ${r.number} criado a partir deste`);
      refresh();
      navigate({ to: "/vendas/pedidos/$id", params: { id: r.id } });
    },
    onError,
  });
  const [emitting, setEmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [canceling, setCanceling] = useState<SalesOrderStatusRef | null>(null);
  const del = useMutation({
    mutationFn: () => api(`/sales-orders/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Pedido excluído");
      refresh();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
      navigate({ to: "/vendas/pedidos" });
    },
    onError,
  });

  const o = order.data;
  const canUpdate = can(perms, "sales_orders", "update");
  const busy = status.isPending || act.isPending;
  // Comissão só vem da API com sellers:read (ou para o usuário-vendedor, a dele): sem ela a coluna some.
  const showCommission = o?.commissionTotalCents != null;
  // Pedido no faturamento agrupado (T2.7): situação presa até desfazer o faturamento.
  const cancelTo = o?.groupedBilling
    ? null
    : (o?.nextStatuses.find((s) => s.baseState === "canceled") ?? null);
  // "Emitir NF-e" (T2.3): sem nota ou com a última em rascunho/rejeitada; pedido cancelado não emite.
  const canEmit =
    !!o &&
    can(perms, "invoices", "create") &&
    o.status.baseState !== "canceled" &&
    !o.groupedBilling &&
    (!o.invoice || o.invoice.status === "draft" || o.invoice.status === "rejected");
  const manual: OrderManualAction[] = o
    ? [
        ...(o.effects.stock !== "posted" && o.status.baseState !== "canceled" ? ["post_stock" as const] : []),
        ...(o.effects.stock !== "none" ? ["reverse_stock" as const] : []),
        ...(o.effects.financial === "none" && o.status.baseState !== "canceled"
          ? ["post_financial" as const]
          : []),
        ...(o.effects.financial === "posted" ? ["reverse_financial" as const] : []),
      ]
    : [];
  return (
    <>
      <ContentHeader
        title={o ? `Pedido de venda nº ${o.number}` : "Pedido de venda"}
        breadcrumb={["Vendas", "Pedidos de venda", o ? String(o.number) : ""]}
        actions={
          o && (
            <>
              <Link to="/vendas/pedidos" className="btn btn-outline-secondary">
                Voltar
              </Link>
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={pdf.isPending}
                onClick={() => pdf.mutate()}
              >
                {pdf.isPending ? "Gerando PDF…" : "Imprimir"}
              </button>
              {canEmit && (
                <button type="button" className="btn btn-primary" onClick={() => setEmitting(true)}>
                  Emitir NF-e
                </button>
              )}
              {can(perms, "sales_orders", "create") && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={duplicate.isPending}
                  onClick={() => duplicate.mutate()}
                >
                  Duplicar
                </button>
              )}
              {canUpdate && !o.lockedReason && (
                <Link
                  to="/vendas/pedidos/$id/editar"
                  params={{ id: o.id }}
                  className="btn btn-outline-primary"
                >
                  Editar
                </Link>
              )}
              {canUpdate && cancelTo && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={busy}
                  onClick={() => setCanceling(cancelTo)}
                >
                  Cancelar pedido
                </button>
              )}
              {can(perms, "sales_orders", "delete") && !o.lockedReason && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDeleting(true)}>
                  Excluir
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={order.isLoading} error={order.error} />
      {o && (
        <div className="row">
          <div className="col-xl-9">
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span>Situação:</span>
                <StatusBadge tone={o.status.color}>{o.status.label}</StatusBadge>
                {canUpdate && !o.groupedBilling && o.nextStatuses.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 ms-md-3" role="group" aria-label="Alterar situação">
                    {o.nextStatuses
                      .filter((s) => s.baseState !== "canceled")
                      .map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={busy}
                          onClick={() => status.mutate(s)}
                        >
                          Marcar como {s.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <dl className="row mb-0">
                <Info label="Cliente">
                  {o.contact.name}
                  {o.contact.document && (
                    <span className="text-muted ms-2">{formatDocument(o.contact.document)}</span>
                  )}
                </Info>
                <Info label="Data">{formatDateBR(o.date)}</Info>
                <Info label="Data prevista">{o.expectedDate && formatDateBR(o.expectedDate)}</Info>
                <Info label="Pedido de compra">{o.customerPo}</Info>
                <Info label="Vendedor">{o.seller?.name}</Info>
                <Info label="Lista de preços">{o.priceList?.name ?? "Preço de venda do produto"}</Info>
                <Info label="Depósito">{o.warehouse?.name ?? "Padrão da empresa"}</Info>
                {o.quote && <Info label="Origem">Proposta comercial nº {o.quote.number}</Info>}
                {o.groupedBilling && (
                  <Info label="Faturamento">
                    {can(perms, "grouped_billing", "read") ? (
                      <Link to="/financas/faturamento-agrupado/$id" params={{ id: o.groupedBilling.id }}>
                        Faturamento agrupado nº {o.groupedBilling.number}
                      </Link>
                    ) : (
                      `Faturamento agrupado nº ${o.groupedBilling.number}`
                    )}
                  </Info>
                )}
                {o.invoice && can(perms, "invoices", "read") && (
                  <Info label="Nota fiscal">
                    <Link to="/vendas/notas-fiscais/$id" params={{ id: o.invoice.id }}>
                      {o.invoice.number === null ? "NF-e (rascunho)" : `NF-e nº ${o.invoice.number}`}
                    </Link>{" "}
                    <StatusBadge tone={INVOICE_STATUS_TONE[o.invoice.status]}>
                      {INVOICE_STATUS_LABEL[o.invoice.status]}
                    </StatusBadge>
                  </Info>
                )}
              </dl>
            </div>
            <div className="box card mb-3">
              <div className="table-responsive">
                <table className="table align-middle mb-0" aria-label="Itens do pedido">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Descrição</th>
                      <th scope="col">Un</th>
                      <th scope="col" className="text-end">
                        Qtd
                      </th>
                      <th scope="col" className="text-end">
                        Preço un.
                      </th>
                      <th scope="col" className="text-end">
                        Desc.
                      </th>
                      <th scope="col" className="text-end">
                        Total
                      </th>
                      {showCommission && (
                        <th scope="col" className="text-end">
                          Comissão
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((it) => (
                      <tr key={it.position}>
                        <td>{it.position}</td>
                        <td>
                          {it.description}
                          {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                        </td>
                        <td>{it.unit ?? "—"}</td>
                        <td className="text-end">{formatQty(it.qtyQ4)}</td>
                        <td className="text-end">{formatBRL(it.listPriceCents)}</td>
                        <td className="text-end">{it.discountPct4 ? formatPct(it.discountPct4) : "—"}</td>
                        <td className="text-end">{formatBRL(it.totalCents)}</td>
                        {showCommission && (
                          <td className="text-end">
                            {it.commissionCents != null && it.commissionPct4 != null
                              ? `${formatBRL(it.commissionCents)} (${formatPct(it.commissionPct4)})`
                              : "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Pagamento e transporte</h2>
              <dl className="row mb-0">
                <Info label="Condição de pagamento">{o.paymentTerm?.name}</Info>
                <Info label="Transportador">{o.carrier?.name}</Info>
                <Info label="Frete">{FREIGHT_MODE_LABELS[o.freightMode]}</Info>
              </dl>
              {o.installments.length > 0 && (
                <table className="table table-sm w-auto mt-2" aria-label="Parcelas">
                  <thead>
                    <tr>
                      <th scope="col">Parcela</th>
                      <th scope="col">Vencimento</th>
                      <th scope="col" className="text-end">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.installments.map((p) => (
                      <tr key={p.number}>
                        <td>{p.number}</td>
                        <td>{formatDateBR(p.dueDate)}</td>
                        <td className="text-end">{formatBRL(p.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {o.notes && (
                <>
                  <h3 className="h6 mt-3">Observações</h3>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {o.notes}
                  </p>
                </>
              )}
              {o.internalNotes && (
                <>
                  <h3 className="h6 mt-3">Observações internas</h3>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {o.internalNotes}
                  </p>
                </>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Histórico de situações</h2>
              <QueryStatus loading={history.isLoading} error={history.error} />
              {history.data && history.data.data.length === 0 && (
                <p className="text-muted small mb-0">Nenhuma mudança de situação ainda.</p>
              )}
              {history.data && history.data.data.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" aria-label="Histórico de situações">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Mudança</th>
                        <th scope="col">Ações executadas</th>
                        <th scope="col">Usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.data.map((h) => (
                        <tr key={h.id}>
                          <td className="text-nowrap">{dateTimeBR(h.createdAt)}</td>
                          <td>{historyLabel(h)}</td>
                          <td>{historySteps(h) || "—"}</td>
                          <td>{h.userName ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <aside className="col-xl-3">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Totais</h2>
              <dl className="row mb-0 small">
                {(
                  [
                    ["Total dos itens", o.itemsTotalCents],
                    [
                      o.discountPct4 !== null ? `Desconto (${formatPct(o.discountPct4)})` : "Desconto",
                      -o.discountCents,
                    ],
                    ["Frete", o.freightCents],
                    ["Outras despesas", o.otherExpensesCents],
                  ] as const
                ).map(([label, v]) => (
                  <Fragment key={label}>
                    <dt className="col-7 fw-normal">{label}</dt>
                    <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                  </Fragment>
                ))}
                <dt className="col-7">Total do pedido</dt>
                <dd className="col-5 text-end fw-bold mb-1" data-testid="order-total">
                  {formatBRL(o.totalCents)}
                </dd>
                {o.commissionTotalCents != null && (
                  <>
                    <dt className="col-7 fw-normal">Comissão</dt>
                    <dd className="col-5 text-end mb-0">{formatBRL(o.commissionTotalCents)}</dd>
                  </>
                )}
              </dl>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Estoque e contas</h2>
              <dl className="row mb-2 small">
                <dt className="col-6 fw-normal">Estoque</dt>
                <dd className="col-6 text-end mb-1" data-testid="order-stock">
                  {STOCK_EFFECT_LABELS[o.effects.stock]}
                </dd>
                <dt className="col-6 fw-normal">Contas</dt>
                <dd className="col-6 text-end mb-0" data-testid="order-financial">
                  {FINANCIAL_EFFECT_LABELS[o.effects.financial]}
                </dd>
              </dl>
              {canUpdate && manual.length > 0 && (
                <div className="d-grid gap-1" role="group" aria-label="Ações de estoque e contas">
                  {manual.map((a) => (
                    <button
                      key={a}
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={busy}
                      onClick={() => act.mutate(a)}
                    >
                      {ORDER_MANUAL_ACTION_LABELS[a]}
                    </button>
                  ))}
                </div>
              )}
              {o.lockedReason && <p className="text-muted small mt-2 mb-0">{o.lockedReason}</p>}
            </div>
            {can(perms, "logistics", "read") && (
              <ShipmentsPanel salesOrderId={o.id} canceled={o.status.baseState === "canceled"} />
            )}
          </aside>
        </div>
      )}
      {o && (
        <EmitInvoiceDialog
          open={emitting}
          orderId={o.id}
          onClose={() => setEmitting(false)}
          onEmitted={(d: FiscalDocumentView) => {
            setEmitting(false);
            refresh();
            navigate({ to: "/vendas/notas-fiscais/$id", params: { id: d.id } });
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        title="Excluir pedido"
        message={`Excluir o pedido nº ${o?.number ?? ""}? Ele pode ser restaurado pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          del.mutate();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
      <ConfirmDialog
        open={!!canceling}
        title="Cancelar pedido"
        message={`Cancelar o pedido nº ${o?.number ?? ""}? O estoque reservado ou lançado e as contas lançadas são estornados.`}
        confirmLabel="Cancelar pedido"
        onConfirm={() => {
          if (canceling) status.mutate(canceling);
          setCanceling(null);
        }}
        onCancel={() => setCanceling(null)}
      />
    </>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9">{children || "—"}</dd>
    </>
  );
}
